(ns manage.adset-template-table.view
  (:require ["@mui/material/Badge$default" :as Badge]
            ["@mui/material/Button$default" :as Button]
            ["@mui/material/Chip$default" :as Chip]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/x-data-grid-premium" :refer [DataGridPremium GridActionsCellItem]]
            ["react" :as react]
            [cljs-bean.core :refer [->clj]]
            [common.modal.util :refer [with-confirmation-modal]]
            [common.queries :refer [use-query]]
            [common.resource.query.adset :as adset-queries]
            [common.util :refer [format-timestamp spread-obj]]
            [common.widgets.data-grid :as dg]
            [manage.adset-template-page.view.templates-panel :as-alias templates-panel]
            [manage.adset-template-page.view.templates-selection :as template-selection]
            [re-frame.core :as rf]
            [uix.core :as uix :refer [$ defui]]
            [uix.re-frame :refer [use-subscribe]]))

(defui format-dimensions
  "Renders the size of the current template revision."
  [{:keys [width height]}]
  (when (and (some? width) (some? height))
    ($ Chip {:label (str width "x" height)})))

(defui format-version
  [{:keys [id revisions]}]
  (let [current-version (-> revisions first :version)
        has-new-version (use-subscribe [:page.adset-template/new-revision? id])]
    ($ Badge (cond-> {:color "primary"
                      :variant "dot"
                      :invisible (not has-new-version)
                      :sx #js {:lineHeight "initial"
                               :p 0.5}}
               has-new-version (assoc :data-testid :has-new-version))
       current-version)))

(defn grid-columns [adset-exported]
  #js [#js {:field "template-name"
            :headerName "Name"
            :sortable true
            :filterable true
            :width 250
            :flex 1
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (:name row))}
       #js {:field "dimensions"
            :headerName "Dimensions"
            :width 100
            :flex 1
            :headerAlign "center"
            :align "center"
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (first (:revisions row)))
            :renderCell (fn [^js params]
                          ($ format-dimensions (.-value params)))}
       #js {:field "version"
            :headerName "Version"
            :width 100
            :flex 1
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (-> row :revisions first :version))
            :renderCell (fn [^js params]
                          ($ format-version (.-row params)))}
       #js {:field "last-modified"
            :headerName "Last modified"
            :sortable true
            :filterable true
            :width 150
            :flex 1
            :valueGetter (fn [_value row ^js _column ^js _apiRef]
                           (:updatedAt row))
            :renderCell (fn [^js params]
                          (format-timestamp (.-value params)))}
       #js {:field "actions"
            :headerName "Actions"
            :type "actions"
            :width 80
            :getActions (fn [^js params]
                          #js [(react/createElement
                                GridActionsCellItem
                                #js {:icon ($ Icon "delete")
                                     :id (.-id params)
                                     :key (.-id params)
                                     :label "Delete"
                                     :onClick (fn []
                                                (with-confirmation-modal adset-exported
                                                  ::templates-panel/warning
                                                  template-selection/delete-template-warning-props
                                                  #(rf/dispatch [:modal/open {:id ::templates-panel/confirm-delete
                                                                              :props {:id (.-id params)}}])))
                                     :showInMenu true})])}])

(defui assign-template-button [{:keys [data-testid adset-exported adset-channel has-assigned-template?]}]
  (let [on-click (uix/use-callback #(rf/dispatch [:modal/open {:id :legacy/add-template-modal}])
                                   [])
        allow-multiple-templates? (template-selection/allow-multiple-templates-selection? adset-channel)]
    ;; We only display the button after we know which label to display.
    (when (some? adset-channel)
      ($ Button
         {:variant "contained"
          :data-testid data-testid
          :disabled (and (not allow-multiple-templates?)
                         has-assigned-template?)
          :onClick (fn []
                     (with-confirmation-modal adset-exported
                       ::templates-panel/warning
                       template-selection/add-template-warning-props
                       on-click))}
         (if allow-multiple-templates?
           "Add Templates"
           "Select Template")))))

(def Toolbar
  (uix/as-react
   (fn [{:keys [adset-exported adset-channel has-assigned-template?]}]
     ($ dg/grid-toolbar
        ($ assign-template-button
           {:adset-exported adset-exported
            :adset-channel adset-channel
            :has-assigned-template? has-assigned-template?
            :data-testid "header-open-templates-modal-button"})))))

(defui template-data-grid []
  (let [{:keys [adset-id]} (use-subscribe [:url-context])
        {templates :data} (use-query (adset-queries/adset-template-query {:adset-id adset-id}))
        {adset-exported :data} (use-query (spread-obj (adset-queries/adset-exports-history-query {:adset-id adset-id})
                                                      #js {:select (fn [^js data]
                                                                     (let [data (->clj data)]
                                                                       (boolean (seq data))))}))
        {{:keys [channel]} :data} (use-query (adset-queries/adset-query {:adset-id adset-id}))

        get-row-id (fn [row] (:id row))

        columns (uix/use-memo (fn []
                                (grid-columns adset-exported))
                              [adset-exported])

        on-row-click (uix/use-callback (fn [^js params]
                                         (let [template-id (-> params .-id)]
                                           (with-confirmation-modal adset-exported
                                             ::templates-panel/warning
                                             template-selection/modify-template-warning-props
                                             #(rf/dispatch [:ocp/open {:id :template-revision/crud
                                                                       :props {:id template-id
                                                                               :template (.-row params)}}]))))
                                       [adset-exported])
        page-size 10]
    ($ DataGridPremium
       {:columns columns

        :rows (to-array templates)
        :getRowId get-row-id
        :onRowClick on-row-click
        :data-testid "table-adset-template"

        :checkboxSelection false
        :disableRowSelectionOnClick true

        :pagination true
        :pageSizeOptions #js [page-size]

        :localeText #js {:noRowsLabel "You have no templates assigned to your adset yet, add one to get started."}

        :initialState #js {:pagination #js {:paginationModel #js {:pageSize page-size}}}

        :slots #js {:row dg/WithDynamicPropsRow
                    :cell dg/WithDynamicPropsCell
                    :toolbar Toolbar}
        :slotProps #js {:baseTextField dg/grid-toolbar-outlined-input
                        :filterPanel #js {:data-testid "filterPanel"
                                          :filterFormProps #js {:data-testid "filterForm"
                                                                :columnInputProps #js {:data-testid "columnInput"}
                                                                :operatorInputProps #js {:data-testid "operatorInput"}
                                                                :valueInputProps #js {:data-testid "valueInput"}}}
                        :row #js {:get-props-fn (fn [{:keys [row]}]
                                                  {:data-testid (:name row)})}
                        :cell #js {:get-props-fn (fn [{:keys [^js column]}]
                                                   {:data-testid (.-field column)})}
                        :toolbar #js {:adset-exported adset-exported
                                      :adset-channel channel
                                      :has-assigned-template? (boolean (seq templates))}}
        :sx #js {:maxHeight "70rem"
                 "& .MuiDataGrid-row:hover" #js {:cursor "pointer"}}})))
